// #711 — shared structured editors for the four new intake topics.
// Lifted out of pages/IntakeReview.js so the Portal section detail
// views can reuse the exact same editor components for inline editing
// (no two copies that drift). The three components and their constants
// are pure UI — they receive `value` + `onChange` and don't touch the
// network — so the parent screen owns persistence and toast handling.
//
// Backend persistence shapes are documented inline. Both
// IntakeReview's PUT /api/intake/review and the new Portal inline-save
// path send the same dict shapes and rely on the matching decompose-
// to-memory-rows logic in app/api/intake.py PUT /review.

import React, { useEffect } from 'react';
import { useLang } from '../i18n/LanguageContext';

// #711 — self-contained styling for the editors. The pre-fix versions
// used .review-field / .review-list-* classes that only existed in
// IntakeReview's <style> block — when the same components rendered
// inside Portal the inputs lost their borders / padding / focus
// colour and looked nothing like the other Portal forms. We inject
// these styles ONCE on first mount via a side effect into the
// document head, scoped via .intake-field-editor wrapper so they
// can't leak to unrelated markup.
const EDITOR_STYLE_ID = 'intake-field-editor-styles-711';
const EDITOR_STYLES = `
  .intake-field-editor .review-field { margin-bottom: 16px; }
  .intake-field-editor .review-field:last-child { margin-bottom: 0; }
  .intake-field-editor .review-field label {
    display: block; font-size: 0.78rem; font-weight: 600;
    color: #888; text-transform: uppercase; letter-spacing: 0.04em;
    margin-bottom: 6px;
  }
  .intake-field-editor .review-field input,
  .intake-field-editor .review-field textarea,
  .intake-field-editor .review-field select {
    width: 100%; border: 1.5px solid #E0D8D0; border-radius: 10px;
    padding: 10px 14px; font-size: 0.95rem; font-family: inherit;
    color: #2C2C2C; background: #FAFAF8;
    outline: none; transition: border-color 0.2s;
    box-sizing: border-box;
  }
  .intake-field-editor .review-field input:focus,
  .intake-field-editor .review-field textarea:focus,
  .intake-field-editor .review-field select:focus {
    border-color: #E8845A; background: #fff;
  }
  .intake-field-editor .review-field textarea {
    resize: vertical; min-height: 80px;
  }
`;

function useInjectEditorStyles() {
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (document.getElementById(EDITOR_STYLE_ID)) return undefined;
    const el = document.createElement('style');
    el.id = EDITOR_STYLE_ID;
    el.textContent = EDITOR_STYLES;
    document.head.appendChild(el);
    // Module-scoped — leave the style in place once mounted; cheap
    // to keep around and avoids re-inject churn across navigations.
    return undefined;
  }, []);
}

// ── Pets ──────────────────────────────────────────────────────────────
//
// PetsEditor stores a list of {name, species, status, note}. status is
// 'current' | 'past'. Backend writes one memory row per pet keyed
// pet_<slug> so Berta can address by name and avoid present-tense
// references to past pets.

export const PET_STATUS_OPTIONS = [
  { value: 'current', label: 'Current' },
  { value: 'past',    label: 'Past' },
];

export function PetsEditor({ pets, onChange }) {
  const { t } = useLang();
  const co = t.intakeReview.common;
  const tr = t.intakeReview.pets;
  useInjectEditorStyles();
  const update = (i, field, value) => {
    const next = [...pets];
    next[i] = { ...next[i], [field]: value };
    onChange(next);
  };
  const remove = (i) => onChange(pets.filter((_, j) => j !== i));
  const add = () => onChange([...pets, {
    name: '', species: '', status: 'current', note: '',
  }]);

  return (
    <div className="intake-field-editor">
      {pets.length === 0 && (
        <p style={{ color: '#999', fontSize: '0.9rem', fontStyle: 'italic', marginBottom: 12 }}>
          {tr.empty}
        </p>
      )}
      {pets.map((p, i) => (
        <div
          key={i}
          style={{
            background: '#FAFAF8', border: '1px solid #E0D8D0',
            borderRadius: 10, padding: 14, marginBottom: 10,
          }}
        >
          <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
            <div style={{ flex: 2 }}>
              <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{tr.name}</label>
              <input
                value={p.name || ''}
                onChange={e => update(i, 'name', e.target.value)}
                placeholder={tr.namePlaceholder}
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{tr.species}</label>
              <input
                value={p.species || ''}
                onChange={e => update(i, 'species', e.target.value)}
                placeholder={tr.speciesPlaceholder}
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{tr.status}</label>
              <select
                value={p.status || 'current'}
                onChange={e => update(i, 'status', e.target.value)}
                style={{ width: '100%' }}
              >
                <option value="current">{tr.statusCurrent}</option>
                <option value="past">{tr.statusPast}</option>
              </select>
            </div>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
              {tr.note} <span style={{ fontWeight: 400, textTransform: 'none', color: '#aaa' }}>{co.optional}</span>
            </label>
            <input
              value={p.note || ''}
              onChange={e => update(i, 'note', e.target.value)}
              placeholder={tr.notePlaceholder}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ marginTop: 10, textAlign: 'right' }}>
            <button
              type="button"
              onClick={() => remove(i)}
              style={{
                background: 'none', border: 'none', color: '#c0392b',
                cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500,
                padding: 0, fontFamily: 'inherit',
              }}
            >{co.remove}</button>
          </div>
        </div>
      ))}
      <button
        className="review-add-btn"
        type="button"
        onClick={add}
        style={{
          background: 'none', border: '1px dashed #E8845A', color: '#E8845A',
          padding: '8px 16px', borderRadius: 8, cursor: 'pointer',
          fontSize: '0.88rem', fontWeight: 600, fontFamily: 'inherit',
        }}
      >{tr.addBtn}</button>
    </div>
  );
}


// ── Faith ─────────────────────────────────────────────────────────────
//
// FaithEditor stores {tradition, importance, practices}. Backend writes
// up to 3 memory rows: identity.faith / identity.faith_importance /
// identity.faith_practices. Defensively accepts a legacy freetext
// string and routes it into `practices` so nothing's lost on upgrade.

export const FAITH_IMPORTANCE_OPTIONS = [
  { value: '',                          label: '(not specified)' },
  { value: 'central',                   label: 'Central to daily life' },
  { value: 'cultural-or-holidays-only', label: 'Cultural / holidays only' },
  { value: 'not-really',                label: 'Not really part of daily life' },
];

// Mirrors the keys recognised by app/services/faith_holidays.py:
// normalise_tradition so picking a suggestion flows cleanly into the
// holiday-acknowledgement path. The HTML <datalist> is a SUGGESTION
// list, not a constraint — freetext is accepted for edge cases
// (Quaker, Greek Orthodox, etc.) and faith_holidays falls through to
// "no proactive holiday" for unknown traditions.
export const TRADITION_SUGGESTIONS = [
  'Catholic',
  'Protestant',
  'Anglican',
  'Methodist',
  'Baptist',
  'Lutheran',
  'Presbyterian',
  'Christian (other)',
  'Orthodox',
  'Jewish',
  'Muslim',
  'Hindu',
  'Buddhist',
  'Sikh',
  'Spiritual but not religious',
  'None / not religious',
];

// `datalistId` kept as a no-op prop for backward compatibility with
// older call sites; the new <select> implementation doesn't need it.
// eslint-disable-next-line no-unused-vars
export function FaithEditor({ value, onChange, datalistId, perspective = 'family' }) {
  // #741 — self-perspective tweaks labels from third-person ("she
  // keeps") to second-person ("you keep") so the form reads
  // naturally for an elderly user filling it in for themselves.
  const isSelf = perspective === 'self';
  const { t } = useLang();
  const co = t.intakeReview.common;
  const tr = t.intakeReview.faith;
  useInjectEditorStyles();
  const v = (value && typeof value === 'object' && !Array.isArray(value))
    ? value
    : { tradition: '', importance: '', practices: (typeof value === 'string' ? value : '') };
  const update = (field, next) => {
    onChange({ ...v, [field]: next });
  };

  // #711 — Tradition is a real <select> rather than an <input list=...>
  // because native <datalist> filters the dropdown to entries matching
  // the current input value — so once a tradition is picked, clicking
  // the dropdown arrow showed only that one option (Bas's 2026-05-16
  // staging report). The "Other (specify)" option toggles a freetext
  // input that handles edge cases (Quaker, Greek Orthodox, family-
  // specific phrasings) without losing the data-quality benefit of
  // the curated list. Storage shape is unchanged — tradition is
  // still a single string the backend reads as-is.
  const knownTraditions = new Set(TRADITION_SUGGESTIONS);
  const currentTradition = v.tradition || '';
  const isCustom = currentTradition !== '' && !knownTraditions.has(currentTradition);
  const selectValue = currentTradition === ''
    ? ''
    : isCustom ? '__other__' : currentTradition;

  const handleSelectChange = (e) => {
    const next = e.target.value;
    if (next === '__other__') {
      // Switching to "Other" leaves the current freetext if it was
      // already custom; otherwise clears so the input is empty.
      update('tradition', isCustom ? currentTradition : '');
    } else {
      update('tradition', next);
    }
  };

  return (
    <div className="intake-field-editor">
      <div className="review-field">
        <label>{tr.tradition} <span style={{fontWeight:400,color:'#aaa'}}>{co.optional}</span></label>
        <select value={selectValue} onChange={handleSelectChange}>
          <option value="">{tr.notSpecified}</option>
          {TRADITION_SUGGESTIONS.map(t => (
            <option key={t} value={t}>{t}</option>
          ))}
          <option value="__other__">{tr.otherSpecify}</option>
        </select>
        {selectValue === '__other__' && (
          <input
            value={currentTradition}
            onChange={e => update('tradition', e.target.value)}
            placeholder={tr.traditionCustomPlaceholder}
            style={{ marginTop: 8 }}
          />
        )}
        <div style={{fontSize:'0.78rem',color:'#888',marginTop:4}}>
          {tr.traditionHelp}
        </div>
      </div>
      <div className="review-field">
        <label>{tr.importance} <span style={{fontWeight:400,color:'#aaa'}}>{co.optional}</span></label>
        <select
          value={v.importance || ''}
          onChange={e => update('importance', e.target.value)}
        >
          <option value="">{tr.notSpecified}</option>
          <option value="central">{tr.importanceCentral}</option>
          <option value="cultural-or-holidays-only">{tr.importanceCultural}</option>
          <option value="not-really">{tr.importanceNotReally}</option>
        </select>
      </div>
      <div className="review-field">
        <label>{isSelf ? tr.practicesSelf : tr.practicesFamily} <span style={{fontWeight:400,color:'#aaa'}}>{co.optional}</span></label>
        <textarea
          value={v.practices || ''}
          onChange={e => update('practices', e.target.value)}
          placeholder={isSelf ? tr.practicesPlaceholderSelf : tr.practicesPlaceholderFamily}
        />
      </div>
    </div>
  );
}


// ── Working life ──────────────────────────────────────────────────────
//
// WorkingLifeEditor stores {profession_or_role, employer, industry,
// years_active, source_of_pride}. Backend writes up to 5 memory rows
// under category=identity. Same legacy-string fallback (lands in
// source_of_pride).

// ── Childhood ─────────────────────────────────────────────────────────
//
// ChildhoodEditor stores {childhood, childhood_sensitive_notes}. The
// `childhood` field is a freetext narrative; the sensitive sub-field
// is write-only by design (the server never round-trips it in
// extracted_data, the editor opens with the field blank, typing
// overwrites the saved value, leaving it blank clears it). Reaches
// Berta only via the PRIVATE GUARD RAILS section in load_memories.
//
// Only used by the Portal section right now (the IntakeReview form
// has its own inline two-textarea block in the Childhood section);
// kept here for parity with the other three editors and to share the
// .intake-field-editor styling.

export function ChildhoodEditor({ value, onChange, perspective = 'family' }) {
  const { t } = useLang();
  const co = t.intakeReview.common;
  const tr = t.intakeReview.childhood;
  useInjectEditorStyles();
  const isSelf = perspective === 'self';
  const v = (value && typeof value === 'object' && !Array.isArray(value))
    ? value
    : { childhood: (typeof value === 'string' ? value : ''), childhood_sensitive_notes: '' };
  const update = (field, next) => {
    onChange({ ...v, [field]: next });
  };
  return (
    <div className="intake-field-editor">
      <div className="review-field">
        <label>{isSelf ? tr.growUpSelf : tr.growUpFamily}</label>
        <textarea
          value={v.childhood || ''}
          onChange={e => update('childhood', e.target.value)}
          placeholder={isSelf ? tr.growUpPlaceholderSelf : tr.growUpPlaceholderFamily}
        />
      </div>
      <div className="review-field">
        <label>
          {tr.sensitiveNotes} <span style={{color:'#B85450', fontWeight:600, fontSize:'0.72rem', textTransform:'uppercase', letterSpacing:'0.04em', marginLeft:6}}>{tr.sensitiveBadge}</span>
        </label>
        <textarea
          value={v.childhood_sensitive_notes || ''}
          onChange={e => update('childhood_sensitive_notes', e.target.value)}
          placeholder={tr.sensitivePlaceholder}
          style={{borderColor: '#E5C5C3', background: '#FBEFEF'}}
        />
        <div style={{fontSize:'0.78rem',color:'#B85450',marginTop:4}}>
          {tr.sensitiveHelp}
        </div>
      </div>
    </div>
  );
}

export function WorkingLifeEditor({ value, onChange, perspective = 'family' }) {
  // #741 — placeholder text and the "Most proud of" pronoun branch on
  // perspective so the self-path reads naturally in first person.
  const isSelf = perspective === 'self';
  const { t } = useLang();
  const co = t.intakeReview.common;
  const tr = t.intakeReview.working;
  useInjectEditorStyles();
  const v = (value && typeof value === 'object' && !Array.isArray(value))
    ? value
    : {
        profession_or_role: '',
        employer: '',
        industry: '',
        years_active: '',
        source_of_pride: (typeof value === 'string' ? value : ''),
      };
  const update = (field, next) => {
    onChange({ ...v, [field]: next });
  };
  return (
    <div className="intake-field-editor">
      <div className="review-field">
        <label>{tr.profession}</label>
        <input
          value={v.profession_or_role || ''}
          onChange={e => update('profession_or_role', e.target.value)}
          placeholder={tr.professionPlaceholder}
        />
      </div>
      <div className="review-field" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div>
          <label>{tr.employer} <span style={{fontWeight:400,color:'#aaa'}}>{co.optional}</span></label>
          <input
            value={v.employer || ''}
            onChange={e => update('employer', e.target.value)}
            placeholder={tr.employerPlaceholder}
          />
        </div>
        <div>
          <label>{tr.industry} <span style={{fontWeight:400,color:'#aaa'}}>{co.optional}</span></label>
          <input
            value={v.industry || ''}
            onChange={e => update('industry', e.target.value)}
            placeholder={tr.industryPlaceholder}
          />
        </div>
      </div>
      <div className="review-field">
        <label>{tr.yearsActive} <span style={{fontWeight:400,color:'#aaa'}}>{co.optional}</span></label>
        <input
          value={v.years_active || ''}
          onChange={e => update('years_active', e.target.value)}
          placeholder={tr.yearsActivePlaceholder}
        />
      </div>
      <div className="review-field">
        <label>{isSelf ? tr.proudSelf : tr.proudFamily} <span style={{fontWeight:400,color:'#aaa'}}>{co.optional}</span></label>
        <textarea
          value={v.source_of_pride || ''}
          onChange={e => update('source_of_pride', e.target.value)}
          placeholder={isSelf ? tr.proudPlaceholderSelf : tr.proudPlaceholderFamily}
        />
      </div>
    </div>
  );
}
